import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../store/auth';
import { useDevices } from '../store/devices';
import { useLiveEvents } from '../hooks/useLiveEvents';
import toast from 'react-hot-toast';

const API = process.env.REACT_APP_SERVER_URL || process.env.REACT_APP_API || '';

export default function FileBrowser() {
  const { deviceId } = useParams();
  const { authHeaders } = useAuth();
  const navigate = useNavigate();
  const { devices } = useDevices();
  const device = devices.find(d => d.device_id === deviceId);
  const deviceName = device ? (device.label || device.hostname || deviceId.slice(0, 8)) : deviceId.slice(0, 8);

  // Default to the user's home directory on Windows
  const defaultPath = device?.username ? `C:\\Users\\${device.username}` : 'C:\\Users';
  const [currentPath, setCurrentPath] = useState(defaultPath);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Editor state
  const [editFile, setEditFile] = useState(null); // { path, content, name, encoding }
  const [editContent, setEditContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [pathInput, setPathInput] = useState(defaultPath);

  const cmd = useCallback(async (endpoint, body) => {
    try {
      const res = await fetch(API + '/cmd/' + deviceId + '/' + endpoint, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {}),
      });
      if (!res.ok) throw new Error('Command failed');
      return true;
    } catch (e) {
      toast.error(e.message);
      return false;
    }
  }, [deviceId, authHeaders]);

  const browse = useCallback((path) => {
    setLoading(true);
    setError('');
    setPathInput(path);
    cmd('files/browse', { path });
  }, [cmd]);

  // Live events
  useLiveEvents(useCallback((e) => {
    if (e.device_id !== deviceId) return;
    if (e.event === 'files_browse_result') {
      setLoading(false);
      if (e.ok) {
        setCurrentPath(e.path);
        setPathInput(e.path);
        setEntries(e.entries || []);
      } else {
        setError(e.error || 'Failed to browse');
        toast.error(e.error || 'Failed to browse');
      }
    } else if (e.event === 'files_read_result') {
      setLoading(false);
      if (e.ok) {
        if (e.encoding === 'text') {
          setEditFile({ path: e.path, name: e.name, encoding: e.encoding });
          setEditContent(e.content);
        } else {
          toast.error('Binary file — cannot edit in browser');
        }
      } else {
        toast.error(e.error || 'Failed to read file');
      }
    } else if (e.event === 'files_write_result') {
      setSaving(false);
      if (e.ok) {
        toast.success('File saved');
      } else {
        toast.error(e.error || 'Failed to save');
      }
    }
  }, [deviceId]));

  // Browse home directory on mount
  useEffect(() => { browse(defaultPath); }, [browse, defaultPath]);

  const openEntry = (entry) => {
    if (entry.is_dir) {
      browse(entry.path);
    } else {
      setLoading(true);
      cmd('files/read', { path: entry.path });
    }
  };

  const saveFile = () => {
    if (!editFile) return;
    setSaving(true);
    cmd('files/write', { path: editFile.path, content: editContent });
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    if (bytes < 1073741824) return (bytes / 1048576).toFixed(1) + ' MB';
    return (bytes / 1073741824).toFixed(1) + ' GB';
  };

  const s = {
    page: { padding: 20, maxWidth: 1200, margin: '0 auto' },
    header: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' },
    pathBar: {
      display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center',
    },
    pathInput: {
      flex: 1, padding: '8px 12px', background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: 6, color: 'var(--text)', fontFamily: 'monospace', fontSize: 13,
    },
    goBtn: {
      padding: '8px 16px', background: '#6366f1', border: 'none', borderRadius: 6,
      color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: 13,
    },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: {
      textAlign: 'left', padding: '8px 12px', fontSize: 11, color: 'var(--muted)',
      textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid var(--border)',
    },
    td: {
      padding: '6px 12px', borderBottom: '1px solid var(--border)', fontSize: 13,
      cursor: 'pointer',
    },
    dirIcon: { color: '#f59e0b', marginRight: 6 },
    fileIcon: { color: '#94a3b8', marginRight: 6 },
    editor: {
      background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8,
      padding: 16,
    },
    textarea: {
      width: '100%', minHeight: 400, padding: 12, background: '#0d0f1a',
      border: '1px solid var(--border)', borderRadius: 6, color: '#e2e8f0',
      fontFamily: 'monospace', fontSize: 13, resize: 'vertical', outline: 'none',
    },
    btn: (bg) => ({
      padding: '6px 14px', borderRadius: 6, border: 'none', cursor: 'pointer',
      background: bg || 'var(--surface)', color: '#fff', fontWeight: 600, fontSize: 12,
    }),
  };

  return (
    <div style={s.page}>
      <div style={s.header}>
        <button onClick={() => navigate(-1)} style={s.btn()}>← Back</button>
        <h2 style={{ fontWeight: 700 }}>File Browser — {deviceName}</h2>
      </div>

      {/* Path bar */}
      <div style={s.pathBar}>
        <input
          style={s.pathInput}
          value={pathInput}
          onChange={e => setPathInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') browse(pathInput); }}
          placeholder="Enter path..."
        />
        <button onClick={() => browse(pathInput)} style={s.goBtn}>Go</button>
        <button onClick={() => browse('C:\\')} style={s.btn('#374151')}>C:\</button>
        <button onClick={() => browse('D:\\')} style={s.btn('#374151')}>D:\</button>
        <button onClick={() => browse(currentPath)} style={s.btn('#374151')}>Refresh</button>
      </div>

      {/* Editor */}
      {editFile && (
        <div style={s.editor}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <strong style={{ flex: 1, fontFamily: 'monospace', fontSize: 13 }}>{editFile.path}</strong>
            <button onClick={saveFile} disabled={saving} style={s.btn('#22c55e')}>
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button onClick={() => setEditFile(null)} style={s.btn('#dc2626')}>Close</button>
          </div>
          <textarea
            style={s.textarea}
            value={editContent}
            onChange={e => setEditContent(e.target.value)}
            spellCheck={false}
          />
        </div>
      )}

      {/* Error */}
      {error && <div style={{ color: '#f87171', padding: 12, marginBottom: 12 }}>{error}</div>}

      {/* Loading */}
      {loading && <div style={{ color: 'var(--muted)', padding: 12 }}>Loading...</div>}

      {/* File table */}
      {!editFile && !loading && (
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Name</th>
              <th style={{ ...s.th, width: 100 }}>Size</th>
              <th style={{ ...s.th, width: 180 }}>Modified</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry, i) => (
              <tr key={i}
                  onClick={() => openEntry(entry)}
                  onDoubleClick={() => openEntry(entry)}
                  style={{ ':hover': { background: 'var(--surface)' } }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <td style={s.td}>
                  <span style={entry.is_dir ? s.dirIcon : s.fileIcon}>
                    {entry.is_dir ? '📁' : '📄'}
                  </span>
                  {entry.name}
                  {entry.error && <span style={{ color: '#f87171', fontSize: 11, marginLeft: 8 }}>({entry.error})</span>}
                </td>
                <td style={{ ...s.td, color: 'var(--muted)', fontSize: 12 }}>
                  {entry.is_dir ? '' : formatSize(entry.size)}
                </td>
                <td style={{ ...s.td, color: 'var(--muted)', fontSize: 12 }}>
                  {entry.modified ? new Date(entry.modified).toLocaleString() : ''}
                </td>
              </tr>
            ))}
            {entries.length === 0 && !loading && (
              <tr><td colSpan={3} style={{ ...s.td, color: 'var(--muted)', textAlign: 'center' }}>Empty directory</td></tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
